/**
 * SkillDelivery Platform — WMS Showcase
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 * PROPRIETARY AND CONFIDENTIAL
 *
 * Content authored by Lalit Ratwani, drawing from 22+ years of
 * Manhattan WMS implementation practice (2003-2026).
 *
 * Static SVG of the 3-layer ML simulation architecture:
 *   Isaac Sim → Unity ML-Agents (PPO + Curiosity) → ONNX/Barracuda
 * Brand-cyan borders on the simulation/training layers, brand-blue on
 * the inference layer to visually mark "this is the output you embed".
 */

import React from 'react';

const MlArchitectureDiagram = () => (
  <section
    aria-label="ML simulation architecture"
    className="mt-12 max-w-2xl mx-auto"
  >
    <svg
      viewBox="0 0 500 320"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-auto"
      role="img"
      aria-label="Three-layer architecture: Isaac Sim, Unity ML-Agents, ONNX Barracuda inference"
    >
      {/* Layer 1 — Simulation */}
      <g>
        <rect
          x="50" y="10" width="400" height="80" rx="8"
          fill="white"
          stroke="#00BCD4"
          strokeWidth="2"
        />
        <text x="250" y="36" textAnchor="middle" fontWeight="bold" fontSize="11" fill="#0097A7" letterSpacing="1.5">
          LAYER 1 · SIMULATION
        </text>
        <text x="250" y="60" textAnchor="middle" fontSize="15" fontWeight="600" fill="#0D2233">
          Isaac Sim
        </text>
        <text x="250" y="80" textAnchor="middle" fontSize="11" fill="#546E7A">
          CLIP zone embeddings · 8-dim per zone
        </text>
      </g>

      {/* Arrow down */}
      <path d="M 250 92 L 250 117" stroke="#B2EBF2" strokeWidth="2" />
      <polygon points="244,117 256,117 250,127" fill="#00BCD4" />

      {/* Layer 2 — Training */}
      <g>
        <rect
          x="50" y="127" width="400" height="80" rx="8"
          fill="white"
          stroke="#00BCD4"
          strokeWidth="2"
        />
        <text x="250" y="153" textAnchor="middle" fontWeight="bold" fontSize="11" fill="#0097A7" letterSpacing="1.5">
          LAYER 2 · TRAINING
        </text>
        <text x="250" y="177" textAnchor="middle" fontSize="15" fontWeight="600" fill="#0D2233">
          Unity ML-Agents
        </text>
        <text x="250" y="197" textAnchor="middle" fontSize="11" fill="#546E7A">
          PPO + Curiosity · 79,000 steps
        </text>
      </g>

      {/* Arrow down */}
      <path d="M 250 209 L 250 234" stroke="#B2EBF2" strokeWidth="2" />
      <polygon points="244,234 256,234 250,244" fill="#2196F3" />

      {/* Layer 3 — Inference (accented in brand-blue) */}
      <g>
        <rect
          x="50" y="244" width="400" height="76" rx="8"
          fill="#F0FAFB"
          stroke="#2196F3"
          strokeWidth="2"
        />
        <text x="250" y="270" textAnchor="middle" fontWeight="bold" fontSize="11" fill="#1976D2" letterSpacing="1.5">
          LAYER 3 · INFERENCE
        </text>
        <text x="250" y="293" textAnchor="middle" fontSize="15" fontWeight="600" fill="#0D2233">
          ONNX Policy
        </text>
        <text x="250" y="312" textAnchor="middle" fontSize="11" fill="#546E7A">
          Unity Barracuda runtime
        </text>
      </g>
    </svg>

    <p className="mt-4 text-center text-sm italic text-brand-muted2">
      Layered architecture. Each layer is independently testable and replaceable.
    </p>
  </section>
);

export default MlArchitectureDiagram;
